// LIBRARIES
import { useContext, useState } from "react";
import Box from "@mui/material/Box";
import { useParams } from "react-router";
import { useForm } from "react-hook-form";

// CUSTOM COMPONENTS
import FdTextField from "../../../components/UIComponents/TextField/FdTextfield";
import FdButton from "../../../components/UIComponents/Button/FdButton";

// STYLE
import { category as classes } from "./styles";

// NOTIFICATION COMPONENT
import { notificationType } from "../../../components/NotificationMessage/NotificationMessage";

// REDUX STORE
import { APIContext } from "../../../store/APIStore";
import { StoreContext } from "../../../store/Store";
import { IAddAttendeeCategory, IAttendeeCategory } from "../../../dataProvider/interfaces/IEvent";

// COMPONENT REFS
interface IComponentProps {
  parentNotificationRef?: any;
}

// COMPONENT CONSTANT
const ERROR_DUPLICATE_CATEGORY = "Category name already exists, try another name.";
const ERROR_MAX_CATEGORY_LEN = "Category name can be Maximum 50 Characters.";
const initialValues: IAttendeeCategory = {
  id: 0,
  name: "",
};

const CategoryForm = ({ parentNotificationRef }: IComponentProps) => {
  const { eventId } = useParams() as { eventId: string };
  const [state, dispatch] = useContext(StoreContext);
  const api = useContext(APIContext);
  const initPayload = state?.attendeeCategoriesList?.action?.payload || initialValues;

  const [isLoading, setLoading] = useState<boolean>(false)

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: initPayload,
  });

  const authCategory = (payload: IAttendeeCategory, list: Array<IAttendeeCategory>): boolean => {
    if (payload?.name?.length > 50) {
      setError("name", { type: "manual", message: ERROR_MAX_CATEGORY_LEN })
      return true;
    }
    const currentAction = state?.attendeeCategoriesList?.action?.type || "";
    const current = payload?.name?.trim().toLowerCase().replace(" ", "");
    if (list && Array.isArray(list) && list.length) {
      const pos = list.findIndex((each) => each?.name?.trim().toLowerCase().replace(" ", "") === current);
      const noEditChange = payload?.id === list[pos]?.id && currentAction === "EDIT";
      if (pos > -1 && !noEditChange) {
        setError("name", { type: "manual", message: ERROR_DUPLICATE_CATEGORY })
        return true;
      }
      clearErrors("name");
    }
    return false;
  };

  const onSubmit = async (values: IAttendeeCategory) => {
    if (values && values?.name?.trim()) {
      setLoading(true);
      const actionType = state?.attendeeCategoriesList?.action?.type || "";
      let current: Array<IAttendeeCategory> = state?.attendeeCategoriesList?.list || [];
      const isAuthCategory = authCategory(values, current);
      if (isAuthCategory) return setLoading(false);
      let isApiSuccess = false;
      try {
        if (actionType === "ADD") {
          const payload: IAddAttendeeCategory = {
            name: values?.name?.trim(),
          }
          const data = await api.eventService.addAttendeeCategory<IAttendeeCategory>(Number(eventId), payload);
          if (data && data.id) {
            isApiSuccess = true;
            current = [...current, { ...data, count: 0 }].sort(
              (a, b) => a.name.localeCompare(b.name)
            );
          }
        }
        if (actionType === "EDIT") {
          const data = await api.eventService.modifyAttendeeCategory<IAttendeeCategory>(Number(eventId), String(values.id), values?.name?.trim());
          if (data && data.id) {
            isApiSuccess = true;
            const pos = current?.findIndex((each) => each.id === values.id);
            if (pos > -1) current[pos] = values;
          }
        }

        if (isApiSuccess) {
          parentNotificationRef.current?.show(
            `Category "${values?.name?.trim()}" ${actionType === "ADD" ? "added" : "updated"} successfully.`,
            notificationType.Success
          );
          dispatch({
            type: "ADD_MODIFY_ATTENDEE_CATEGORIES_LIST",
            payload: { list: [...current], action: { type: "", payload: initialValues } }
          });
        }
      } catch (error: any) {
        dispatch({
          type: "ADD_MODIFY_ATTENDEE_CATEGORIES_LIST",
          payload: { action: { type: "", payload: initialValues } }
        });
        console.log(error.response);
        let errorMessage = error?.response?.data?.message || "";
        if (errorMessage?.includes("Duplicate")) errorMessage = ERROR_DUPLICATE_CATEGORY;
        parentNotificationRef.current?.show(
          errorMessage,
          notificationType.Error
        );
      }
      setLoading(false)
    }
    reset();
  };

  const onCancel = () => {
    dispatch({
      type: "ADD_MODIFY_ATTENDEE_CATEGORIES_LIST",
      payload: { action: { type: "", payload: initialValues } }
    });
    reset();
  }

  // RENDER CONSTANTS
  const actionType = state?.attendeeCategoriesList?.action?.type || "";

  return (
    <Box sx={classes.innerDiv}>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <form
          id="categoryForm"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          style={{ width: "100%", textAlign: "left" }}
        >
          <Box sx={classes.formRoot}>
            {/* Category name */}
            <Box marginBottom="15px">
              <FdTextField
                register={register}
                name="name"
                label="Category name"
                required
                error={!!errors.name}
                errorMsg={String(errors?.name?.message || "") || (errors.name && "Category name is required")}
              />
            </Box>
          </Box>
        </form>
      </Box>
      <Box>
        <FdButton variant="text" ButtonName="Cancel" onClick={onCancel} />
        <FdButton
          form="categoryForm"
          type="submit"
          ButtonName={actionType === "ADD" ? "Add" : "Save"}
          size="small"
          isLoading={isLoading}
          disabled={isLoading}
        />
      </Box>
    </Box>
  );
};

export default CategoryForm;
